import { useQuery } from "@tanstack/react-query";
import { Flex, Milestones, Spinner, StatusBanner, type MilestoneItem } from "@wayflyer/flyui";
import type { FundingApplicationSchemas } from "api-types";
import { useIntl } from "react-intl";

import { useSpaNavigate } from "../../hooks/use-spa-navigate";
import { getDealOwnerQueryOptions } from "../../offers/queries";

import { MESSAGES } from "./message-definitions";
import {
  SUBSEQUENT_FUNDING_DATA_PULL_PROGRESS_KEY,
  SUBSEQUENT_FUNDING_DATA_PULL_PROGRESS_WINDOW_SECONDS,
  useInReviewStepStatus,
  type FundingApplicationReviewDetailsElapsedTime,
} from "./queries";

export interface ReviewScreenStepsProps {
  customerDomain: string;
  productRequestId: string;
  reviewData: FundingApplicationReviewDetailsElapsedTime;
  airsData: FundingApplicationSchemas.AdditionalInformationRequestsData;
}

/**
 * ReviewScreenSteps component
 */
export const ReviewScreenSteps = ({
  customerDomain,
  productRequestId,
  airsData,
  reviewData,
}: ReviewScreenStepsProps) => {
  const intl = useIntl();
  const { data: stepStatus, isLoading: isStepStatusLoading } = useInReviewStepStatus(customerDomain);
  const { data: dealOwnerData, isLoading: isDealOwnerLeading } = useQuery(
    getDealOwnerQueryOptions(customerDomain, productRequestId)
  );
  const isRepOwned = dealOwnerData && !!dealOwnerData.name;
  const { navigate } = useSpaNavigate("FundingApp");

  if (isStepStatusLoading || isDealOwnerLeading) {
    return (
      <Flex justify="center" width="100%">
        <Spinner size={32} />
      </Flex>
    );
  }

  const airBanner = airsData.airs.length ? (
    <StatusBanner
      variant={airsData.hasActiveAirs ? "warning" : "positive"}
      title={airsData.hasActiveAirs ? intl.formatMessage(MESSAGES.reviewScreenAirOnHoldBannerHeading) : undefined}
      subheading={intl.formatMessage(
        airsData.hasActiveAirs ? MESSAGES.reviewScreenAirOnHoldBannerText : MESSAGES.reviewScreenAirsSubmittedBanner,
        { count: airsData.airs.length }
      )}
      action={{
        content: intl.formatMessage(MESSAGES.reviewScreenAirBannerCTAText, { count: airsData.airs.length }),
        onAction: () => navigate("/in-review/additional-information-request"),
      }}
    />
  ) : null;

  const manualAirBanner = airsData.hasActiveManualAirs ? (
    <StatusBanner
      variant="warning"
      title={intl.formatMessage(MESSAGES.reviewScreenAirOnHoldBannerHeading)}
      subheading={
        isRepOwned
          ? intl.formatMessage(MESSAGES.reviewScreenManualAirOnHoldBannerRepOwnedText)
          : intl.formatMessage(MESSAGES.reviewScreenManualAirOnHoldBannerSelfServeText)
      }
    />
  ) : null;

  // for subsequent funding we want to show that progress is being made by letting the data pull status show as active,
  // even if it has already been completed, for 10s (until the next poll of BE)
  let forceDataPullInProgress = false;
  if (
    reviewData.isSubsequentFunding &&
    reviewData.productRequestId &&
    !airsData.airs.length &&
    reviewData.elapsedSeconds
  ) {
    if (reviewData.elapsedSeconds < SUBSEQUENT_FUNDING_DATA_PULL_PROGRESS_WINDOW_SECONDS) {
      const dataPullProgress = localStorage.getItem(
        SUBSEQUENT_FUNDING_DATA_PULL_PROGRESS_KEY.replace(":productRequestId", "" + reviewData.productRequestId)
      );
      if (!dataPullProgress) {
        localStorage.setItem(
          SUBSEQUENT_FUNDING_DATA_PULL_PROGRESS_KEY.replace(":productRequestId", "" + reviewData.productRequestId),
          "true"
        );
        forceDataPullInProgress = true;
      }
    }
  }

  const getMilestoneStatus = (
    stepKey: keyof FundingApplicationSchemas.FundingApplicationReviewState
  ): MilestoneItem["status"] | undefined => {
    const stateToStatusMap: Partial<Record<FundingApplicationSchemas.ReviewStepState, MilestoneItem["status"]>> = {
      ["pending"]: "warning",
      ["action-needed"]: "warning",
      ["completed"]: "positive",
    };
    return stepStatus ? stateToStatusMap[stepStatus[stepKey]] : undefined;
  };

  const shouldSwapStages = stepStatus?.offersState === "completed" && stepStatus?.underwritingState !== "completed";

  const getStages = (): MilestoneItem[] => {
    const baseStages: MilestoneItem[] = [
      {
        id: "dataPull",
        status: !forceDataPullInProgress ? getMilestoneStatus("dataPullState") : undefined,
        isCompleted: stepStatus?.dataPullState === "completed" && !forceDataPullInProgress,
        label: intl.formatMessage(MESSAGES.reviewScreenPullingDataStep),
        slots: { description: intl.formatMessage(MESSAGES.reviewScreenPullingDataStepDetails) },
      },
      {
        id: "dataValidation",
        status: getMilestoneStatus("dataValidationState"),
        isCompleted: ["completed", "pending", "action-needed"].includes(stepStatus?.dataValidationState ?? ""),
        label: intl.formatMessage(MESSAGES.reviewScreenCheckingDataStep),
        slots: {
          description: (
            <>
              {intl.formatMessage(MESSAGES.reviewScreenCheckingDataStepDetails)}
              {airBanner || manualAirBanner ? (
                <Flex direction="column" gap="4">
                  {airBanner}
                  {manualAirBanner}
                </Flex>
              ) : undefined}
            </>
          ),
        },
      },
    ];

    const underwritingStage: MilestoneItem = {
      id: "underwriting",
      status: getMilestoneStatus("underwritingState"),
      isCompleted: stepStatus?.underwritingState === "completed",
      label: intl.formatMessage(MESSAGES.reviewScreenFinancialAssessmentStep),
      slots: { description: intl.formatMessage(MESSAGES.reviewScreenFinancialAssessmentStepDetails) },
    };

    const offersStage: MilestoneItem = {
      id: "offers",
      status: getMilestoneStatus("offersState"),
      isCompleted: stepStatus?.offersState === "completed",
      label: intl.formatMessage(MESSAGES.reviewScreenPreparingOffersStep),
      slots: {
        description: intl.formatMessage(MESSAGES.reviewScreenPreparingOffersStepDetails),
      },
    };

    if (shouldSwapStages) {
      return [...baseStages, offersStage, underwritingStage];
    }

    return [...baseStages, underwritingStage, offersStage];
  };

  const stages = getStages();
  const currentStage = stages.find((stage) => stage.status !== "positive")?.id;

  return <Milestones steps={stages} currentStepId={currentStage ?? ""} />;
};
